import React, { useEffect, useMemo, useState } from "react";
import SidebarField from "../../components/Sidebar/SidebarField";
import EarningDetails from "./Orders/EarningDetails";
import earningApis from "../../apis/earning.apis";
import { formattedAmount } from "../../utils/helpers/Functions.helper";

const getDecimalValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "object" && value?.$numberDecimal) {
    return Number(value.$numberDecimal);
  }

  const parsedValue = Number(value);
  return Number.isNaN(parsedValue) ? null : parsedValue;
};

const formatAmount = (value) => {
  const amount = getDecimalValue(value);
  return amount === null ? "N/A" : formattedAmount(amount);
};

const formatDate = (value) => {
  if (!value) {
    return "N/A";
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const getVendorId = (earningData) =>
  earningData?.orderDetails?.vendor?._id ||
  earningData?.orderDetails?.vendor?.vendor_id ||
  earningData?.vendor?._id ||
  earningData?.vendor?.vendor_id ||
  earningData?.vendor_id?._id ||
  earningData?.vendor_id ||
  earningData?.vendorId;

const getVendorName = (vendor) => {
  const firstName = vendor?.first_name || "";
  const lastName = vendor?.last_name || "";
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName || "N/A";
};

const getPaidByName = (user) => {
  const firstName = user?.first_name || "";
  const lastName = user?.last_name || "";
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName || user?.email || "N/A";
};

const getPayoutId = (payout) => payout?._id || payout?.payoutId || payout?.id;

const InfoCard = ({ label, value }) => (
  <div className="rounded-lg border p-3">
    <p className="text-xs uppercase tracking-wide text-gray500">{label}</p>
    <p className="mt-1 text-sm font-medium break-words">{value || "N/A"}</p>
  </div>
);

const PersonCard = ({ title, person, name }) => (
  <div className="rounded-xl border p-4">
    <p className="text-xs uppercase tracking-wide text-gray500">{title}</p>
    <p className="mt-2 text-sm font-semibold">{name || "N/A"}</p>
    <p className="mt-1 text-sm break-all">{person?.email || "N/A"}</p>
    <p className="mt-1 text-sm">{person?.phone_number || "N/A"}</p>
  </div>
);

const OrderCard = ({ order }) => {
  const address = order?.delivery_address;
  const addressText = [
    address?.flat_no,
    address?.street,
    address?.landmark,
    address?.pin_code,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="rounded-xl border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-gray500">Order Id</p>
          <p className="mt-1 text-sm font-semibold">{order?.order_id || "N/A"}</p>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-wide text-gray500">Payment</p>
          <p className="mt-1 text-sm font-semibold">
            {order?.pay_type || "N/A"} / {order?.payment_status || "N/A"}
          </p>
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <InfoCard label="Order Date" value={formatDate(order?.order_date)} />
        <InfoCard label="Net Total" value={formatAmount(order?.net_total)} />
        <InfoCard label="Item Price" value={formatAmount(order?.item_price)} />
        <InfoCard
          label="Delivery Price"
          value={formatAmount(order?.total_delivery_price)}
        />
      </div>

      <div className="mt-4 rounded-lg bg-gray100 p-3">
        <p className="text-xs uppercase tracking-wide text-gray500">Customer</p>
        <p className="mt-1 text-sm font-semibold">{address?.name || "N/A"}</p>
        <p className="mt-1 text-sm">{address?.phone_number || "N/A"}</p>
        <p className="mt-2 text-sm break-words">{addressText || "N/A"}</p>
      </div>
    </div>
  );
};

const VendorPayoutDetailsView = ({ details }) => {
  const payout = details?.payout;
  const summary = details?.summary;

  if (!payout) {
    return null;
  }

  return (
    <div className="space-y-5">
      <div className="grid gap-3 md:grid-cols-2">
        <InfoCard label="Payout Id" value={payout?._id} />
        <InfoCard label="Status" value={payout?.status} />
        <InfoCard label="Payout Date" value={formatDate(payout?.payout_date)} />
        <InfoCard label="Payment Method" value={payout?.payment_method} />
        <InfoCard
          label="Transaction Reference"
          value={payout?.transaction_reference || "N/A"}
        />
        <InfoCard
          label="Total Payout Amount"
          value={formatAmount(payout?.total_amount)}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <PersonCard
          title="Vendor"
          person={payout?.vendor_id}
          name={getVendorName(payout?.vendor_id)}
        />
        <PersonCard
          title="Paid By"
          person={payout?.paid_by}
          name={getPaidByName(payout?.paid_by)}
        />
      </div>

      {summary ? (
        <div className="space-y-3">
          <p className="text-sm font-semibold">Summary</p>
          <div className="grid gap-3 md:grid-cols-2">
            <InfoCard label="Total Orders" value={String(summary?.totalOrders || 0)} />
            <InfoCard
              label="Total Net Total"
              value={formatAmount(summary?.totalNetTotal)}
            />
            <InfoCard
              label="Total Item Price"
              value={formatAmount(summary?.totalItemPrice)}
            />
            <InfoCard
              label="Total Delivery Price"
              value={formatAmount(summary?.totalDeliveryPrice)}
            />
            <InfoCard label="Total Tips" value={formatAmount(summary?.totalTips)} />
            <InfoCard
              label="Total Payout Amount"
              value={formatAmount(summary?.totalPayoutAmount)}
            />
          </div>
        </div>
      ) : null}

      <div className="space-y-3">
        <p className="text-sm font-semibold">
          Orders ({Array.isArray(payout?.orders) ? payout.orders.length : 0})
        </p>
        {Array.isArray(payout?.orders) && payout.orders.length ? (
          <div className="space-y-3">
            {payout.orders.map((order) => (
              <OrderCard key={order?._id || order?.order_id} order={order} />
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed p-4 text-sm">
            No orders found.
          </div>
        )}
      </div>
    </div>
  );
};

const VendorPayoutsContent = ({ earningData }) => {
  const [payouts, setPayouts] = useState([]);
  const [selectedPayoutDetails, setSelectedPayoutDetails] = useState(null);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const vendorId = useMemo(() => getVendorId(earningData), [earningData]);

  useEffect(() => {
    let isMounted = true;

    const fetchVendorPayouts = async () => {
      if (!vendorId) {
        if (isMounted) {
          setPayouts([]);
        }
        return;
      }

      setIsLoadingList(true);
      setSelectedPayoutDetails(null);

      try {
        const response = await earningApis.getAllVendorPayouts({
          vendor_id: vendorId,
          page: 1,
          limit: 10,
          sortOrder: "desc",
        });

        if (isMounted) {
          setPayouts(response?.data?.data?.payouts || []);
        }
      } catch (error) {
        if (isMounted) {
          setPayouts([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingList(false);
        }
      }
    };

    fetchVendorPayouts();

    return () => {
      isMounted = false;
    };
  }, [vendorId]);

  const handlePayoutClick = async (payoutId) => {
    if (!payoutId) {
      return;
    }

    setIsLoadingDetails(true);

    try {
      const response = await earningApis.getVendorPayoutDetailsById(payoutId);
      setSelectedPayoutDetails(response?.data?.data || null);
    } catch (error) {
      setSelectedPayoutDetails(null);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  if (!vendorId) {
    return (
      <div className="rounded-lg border border-dashed p-4 text-sm">
        Please select an order with a vendor first.
      </div>
    );
  }

  if (selectedPayoutDetails) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => setSelectedPayoutDetails(null)}
          className="rounded-md border px-3 py-2 text-sm font-medium"
        >
          Back To Payouts
        </button>
        <VendorPayoutDetailsView details={selectedPayoutDetails} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border p-4">
        <p className="text-xs uppercase tracking-wide text-gray500">Vendor</p>
        <p className="mt-1 text-sm font-semibold">
          {getVendorName(earningData?.orderDetails?.vendor || earningData?.vendor)}
        </p>
        <p className="mt-1 text-xs break-all">
          {earningData?.orderDetails?.vendor?.email || earningData?.vendor?.email || "N/A"}
        </p>
      </div>

      {isLoadingList ? (
        <div className="rounded-lg border border-dashed p-4 text-sm">
          Loading payouts...
        </div>
      ) : payouts.length ? (
        <div className="space-y-3">
          {payouts.map((payout) => (
            <button
              key={getPayoutId(payout)}
              type="button"
              onClick={() => handlePayoutClick(getPayoutId(payout))}
              disabled={isLoadingDetails}
              className="w-full rounded-xl border p-4 text-left transition hover:border-green600 disabled:cursor-not-allowed"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray500">
                    Payout Id
                  </p>
                  <p className="mt-1 text-sm font-semibold break-all">
                    {payout?._id}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wide text-gray500">
                    Status
                  </p>
                  <p className="mt-1 text-sm font-semibold">{payout?.status}</p>
                </div>
              </div>

              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <InfoCard
                  label="Payout Date"
                  value={formatDate(payout?.payout_date)}
                />
                <InfoCard
                  label="Total Amount"
                  value={formatAmount(payout?.total_amount)}
                />
                <InfoCard
                  label="Payment Method"
                  value={payout?.payment_method || "N/A"}
                />
                <InfoCard
                  label="Transaction Reference"
                  value={payout?.transaction_reference || "N/A"}
                />
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed p-4 text-sm">
          No payouts found for this vendor.
        </div>
      )}

      {isLoadingDetails ? (
        <div className="rounded-lg border border-dashed p-4 text-sm">
          Loading payout details...
        </div>
      ) : null}
    </div>
  );
};

const EarningModal = ({
  handleClose,
  isEarningDetail,
  earningData,
  modalType,
}) => {
  if (!isEarningDetail) {
    return null;
  }

  return (
    <SidebarField
      footer={false}
      handleClose={() => handleClose()}
      title={modalType === "vendorPayouts" ? "Vendor Payouts" : "Order Details"}
    >
      <div className="px-2">
        {modalType === "vendorPayouts" ? (
          <VendorPayoutsContent earningData={earningData} />
        ) : (
          <EarningDetails
            deliveryDetails={earningData?.orderId?.delivery_address}
            vendorDetails={earningData?.orderDetails?.vendor}
            ProductDeatils={earningData?.orderDetails}
            earningData={earningData}
          />
        )}
      </div>
    </SidebarField>
  );
};

export default EarningModal;
